import { useHistory } from 'react-router-dom';
import { APP_ROOT } from 'Utils/EnvVars';
import useSession from './useSession';

/**
 * @returns Fetch wrapper for the VIP API. Handles some common things such as Session ID and access token.
 */
function useVipApiFetcher() {
  const { sessionId } = useSession();
  const history = useHistory();

  const missingSessionId = sessionId === null || sessionId === undefined;

  /**
   * @param {String} url
   * @param {String} method
   * @param {BodyInit} body
   */
  const wrappedFetch = async (url, method = 'GET', body = undefined) => {

    if (missingSessionId) throw 'VIP API fetcher not ready: Missing sessionId';

    const headers = {
      'session-id': sessionId,
      'accept': 'application/json',
      'content-type': body && 'application/json',
      'authorization': 'PatronSession',
      'ipAddress': window.sessionStorage.getItem("clientIpAddress")
    };

    if (window.testGatewayThrow) {
      headers['test-gateway-throw'] = window.testGatewayThrow;
    }

    return window.fetch(APP_ROOT + url, {
      method,
      headers,
      body: JSON.stringify(body)
    });
  };


  const fetchData = async (url, method = 'GET', body = undefined) => {

    let error;

    const showError = (message) => {
      history.push({
        pathname: '/error',
        state: {
          isError: true,
          message,
          showButton: true,
          allowRetry: false
        }
      });
    }

    try {
      var response = await wrappedFetch(url, method, body);
    } catch (ex) {
      // Catastrophic, unknown error.
      showError();
      return;
    }

    if (response.ok) {
      if (response.headers.get('content-length') === 0){
        return {};
      }
      return await response.json();
    } else {
      if(response.status === 401) {
        history.push("/expired");
        return;
      }

      error = await response.text();
      error = error.replace(/\'/g, ''); // Remove escaped doublequotes from JSON
      showError(error);
    }
  };

  return {
    /**
     * Raw fetch function, returns Response promise from window.fetch.
     * @returns {Response} Response
     */
    fetch: wrappedFetch,
    ready: !missingSessionId,
    /**
     * Fetch with common error handling logic.
     * @returns {Object} Deserialized object.
     */
    fetchData
  };
}

export default useVipApiFetcher;

export const handleError = (error, history, customErrorButtons) => {
  console.error(error);
  if(customErrorButtons) {
    history.push({
      pathname: '/error',
      state: {
        isError: true,
        showButton: true,
        allowRetry: false,
        customButtons: customErrorButtons,
        transactionInitiated: true
      }});
  } else {
    history.push({
      pathname: '/error'
    });
  }
}

export const handleResponse = (history, response) => {
  if (response.status === 500) {
    throw new Error(response.statusText);
  } else if (response.status === 401) {
    history.push({
      pathname: '/expired'
    });
    return;
  }
  return response.json().then(data => ({ status: response.status, body: data }));
}

export const handleOkResponse = ({ history, response, title, message, navigateTo, customButtons, errorCustomButtons, transactionInitiated = false }) => {
  if (response.status === 200) {
    history.push({
      pathname: '/success',
      state: {
        isError: false,
        title: title,
        message: message,
        navigateTo: navigateTo,
        customButtons: customButtons,
      }
    });
  } else {
    //const responseMessage = typeof response.body  === "string" ? response.body : undefined;
    //console.error(response);
    history.push({
      pathname: "/error",
      state: {
        isError: true,
        message:
          response.body.patronErrorMessage ?? "We are unable to complete this request. Please contact our Customer Service Team at 1-800-500-1973. Reference code E-29 so that we may better serve you.",
        showButton: true,
        allowRetry: false,
        customButtons: errorCustomButtons,
        transactionInitiated: transactionInitiated,
      },
    });
  }
}